import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError, relTime, type TemplateDetail } from '../lib/api'

/**
 * Reusable templates. List view with create; a detail/editor route for each. Copy lives here
 * once and is referenced by slug at send time, so a subject wording change ships everywhere at
 * once instead of being pasted into every integration.
 */
export function Templates() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showDisabled, setShowDisabled] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['templates', showDisabled],
    queryFn: () => api.templates.list(showDisabled),
  })
  const rows = data?.data ?? []

  const seed = useMutation({
    mutationFn: () => api.templates.seedSamples(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  })

  return (
    <div>
      <div className="spread" style={{ alignItems: 'center' }}>
        <h1>Templates</h1>
        <div className="row" style={{ gap: 8 }}>
          <button className="secondary" disabled={seed.isPending} onClick={() => seed.mutate()}>
            {seed.isPending ? 'Adding…' : 'Add samples'}
          </button>
          <button onClick={() => navigate('/templates/new')}>New template</button>
        </div>
      </div>
      <p className="lede">
        Reusable email copy with <span className="mono">{'{{variables}}'}</span>. Send by slug and
        pass the variables at send time.
      </p>

      <label className="row small muted" style={{ marginBottom: 12, gap: 6 }}>
        <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
        Show disabled
      </label>

      {isLoading && <div className="skeleton" style={{ height: 160 }} />}

      {!isLoading && rows.length === 0 && (
        <div className="empty">
          <div className="big">✉</div>
          <p><strong>No templates yet</strong></p>
          <p>Start from our ready-made pack (welcome, verify email, password reset, order
            confirmation, receipt) or build your own.</p>
          <div className="row" style={{ gap: 8, marginTop: 10, justifyContent: 'center' }}>
            <button disabled={seed.isPending} onClick={() => seed.mutate()}>
              {seed.isPending ? 'Adding…' : 'Add sample templates'}
            </button>
            <button className="secondary" onClick={() => navigate('/templates/new')}>New template</button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card flush">
          <table className="data">
            <thead>
              <tr><th>Name</th><th>Slug</th><th>Body</th><th>Variables</th><th>Updated</th><th /></tr>
            </thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} style={{ opacity: t.active ? 1 : 0.55, cursor: 'pointer' }}
                    onClick={() => navigate(`/templates/${t.id}`)}>
                  <td className="strong">
                    {t.name}{!t.active && <span className="badge neutral" style={{ marginLeft: 6 }}>disabled</span>}
                    {t.description && <div className="small muted">{t.description}</div>}
                  </td>
                  <td className="mono small">{t.slug}</td>
                  <td className="small muted">
                    {t.has_html && <span className="badge info">HTML</span>}{' '}
                    {t.has_text && <span className="badge neutral">text</span>}
                  </td>
                  <td className="small mono muted">{t.placeholders.length ? t.placeholders.join(', ') : '—'}</td>
                  <td className="small muted">v{t.version} · {relTime(t.updated_at)}</td>
                  <td className="right"><span className="link">Edit →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const BLANK: Partial<TemplateDetail> = { name: '', slug: '', subject: '', html: '', text: '', description: '' }

export function TemplateDetailPage() {
  const { id } = useParams()
  const isNew = id === 'new'
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: () => api.templates.get(id!),
    enabled: !isNew && !!id,
  })

  const [form, setForm] = useState<Partial<TemplateDetail>>(BLANK)
  const [error, setError] = useState<ApiError | null>(null)
  const [preview, setPreview] = useState<{ subject: string; html: string | null; text: string | null } | null>(null)
  const [vars, setVars] = useState<Record<string, string>>({})

  useEffect(() => { if (data) setForm(data) }, [data])

  const placeholders = useMemo(() => {
    const found = new Set<string>()
    for (const s of [form.subject, form.html, form.text]) {
      if (!s) continue
      for (const m of s.matchAll(/\{\{\s*([A-Za-z0-9_.\-]+)\s*\}\}/g)) { if (m[1]) found.add(m[1]) }
    }
    return [...found]
  }, [form.subject, form.html, form.text])

  const save = useMutation({
    mutationFn: () => isNew
      ? api.templates.create({
          name: form.name!, subject: form.subject!, slug: form.slug || undefined,
          html: form.html || undefined, text: form.text || undefined, description: form.description || undefined,
        })
      : api.templates.update(id!, {
          name: form.name, subject: form.subject,
          html: form.html ?? '', text: form.text ?? '', description: form.description ?? '',
        }),
    onSuccess: (t) => {
      setError(null)
      qc.invalidateQueries({ queryKey: ['templates'] })
      qc.invalidateQueries({ queryKey: ['template', id] })
      if (isNew) navigate(`/templates/${(t as any).id}`)
    },
    onError: (e) => setError(e as ApiError),
  })

  const disable = useMutation({
    mutationFn: () => form.active ? api.templates.disable(id!) : api.templates.enable(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['template', id] }); qc.invalidateQueries({ queryKey: ['templates'] }) },
  })
  const remove = useMutation({
    mutationFn: () => api.templates.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); navigate('/templates') },
  })
  const runPreview = useMutation({
    mutationFn: () => api.templates.preview(id!, vars),
    onSuccess: (p) => setPreview(p),
  })

  const set = (k: keyof TemplateDetail) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  if (!isNew && isLoading) return <div className="skeleton" style={{ height: 300 }} />

  return (
    <div>
      <button className="ghost" onClick={() => navigate('/templates')} style={{ marginBottom: 12 }}>← Templates</button>
      <div className="spread" style={{ alignItems: 'center' }}>
        <h1>{isNew ? 'New template' : form.name}</h1>
        {!isNew && (
          <div className="row" style={{ gap: 8 }}>
            <button className="ghost" onClick={() => disable.mutate()}>
              {form.active ? 'Disable' : 'Enable'}
            </button>
            <button className="ghost danger" onClick={() => {
              if (confirm('Delete this template permanently?')) remove.mutate()
            }}>Delete</button>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <form onSubmit={e => { e.preventDefault(); save.mutate() }}>
          <div className="field">
            <label>Name</label>
            <input value={form.name ?? ''} onChange={set('name')} placeholder="Welcome email" />
          </div>
          {isNew && (
            <div className="field">
              <label>Slug <span className="muted">(optional — derived from name)</span></label>
              <input value={form.slug ?? ''} onChange={set('slug')} placeholder="welcome-email" className="mono" />
            </div>
          )}
          <div className="field">
            <label>Subject</label>
            <input value={form.subject ?? ''} onChange={set('subject')} placeholder="Welcome, {{name}}!" />
          </div>
          <div className="field">
            <label>HTML body</label>
            <textarea rows={8} value={form.html ?? ''} onChange={set('html')} className="mono"
                      placeholder="<h1>Hi {{name}}</h1>" />
          </div>
          <div className="field">
            <label>Text body</label>
            <textarea rows={5} value={form.text ?? ''} onChange={set('text')} className="mono"
                      placeholder="Hi {{name}}" />
          </div>
          <div className="field">
            <label>Description <span className="muted">(optional)</span></label>
            <input value={form.description ?? ''} onChange={set('description')} />
          </div>

          {placeholders.length > 0 && (
            <div className="hint" style={{ marginBottom: 12 }}>
              Variables: {placeholders.map(p => <span key={p} className="mono" style={{ marginRight: 8 }}>{`{{${p}}}`}</span>)}
            </div>
          )}
          {error && <div className="alert error" style={{ marginBottom: 12 }}>{error.message}</div>}

          <button type="submit" disabled={!form.name || !form.subject || save.isPending}>
            {save.isPending ? 'Saving…' : isNew ? 'Create template' : 'Save changes'}
          </button>
        </form>
      </div>

      {!isNew && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Preview</h2>
          {placeholders.length > 0 && (
            <div className="field">
              <label>Sample variables</label>
              {placeholders.map(p => (
                <div key={p} className="row" style={{ gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <span className="mono small" style={{ minWidth: 120 }}>{`{{${p}}}`}</span>
                  <input value={vars[p] ?? ''} onChange={e => setVars(v => ({ ...v, [p]: e.target.value }))}
                         placeholder={p} />
                </div>
              ))}
            </div>
          )}
          <button className="secondary" onClick={() => runPreview.mutate()} disabled={runPreview.isPending}>
            {runPreview.isPending ? 'Rendering…' : 'Render preview'}
          </button>

          {preview && (
            <div style={{ marginTop: 16 }}>
              <table className="kv boxed"><tbody>
                <tr><td>Subject</td><td>{preview.subject}</td></tr>
              </tbody></table>
              {preview.html && (
                <>
                  <div className="small muted" style={{ margin: '12px 0 4px' }}>HTML</div>
                  <iframe title="preview" srcDoc={preview.html} style={{ width: '100%', height: 320, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }} />
                </>
              )}
              {preview.text && (
                <>
                  <div className="small muted" style={{ margin: '12px 0 4px' }}>Text</div>
                  <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{preview.text}</pre>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
